import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, Rectangle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import LocalLakesLayer from './LocalLakesLayer';
import { searchManagedGeographyFeatures } from '../../services/geographyService';

function LakeMapActions({ geometry, boxMode, onBox, onDone }) {
  const map = useMap();
  const start = useRef(null);
  const [box, setBox] = useState(null);
  useEffect(() => {
    if (!geometry?.coordinates?.length) return;
    const bounds = L.geoJSON(geometry).getBounds();
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
  }, [geometry, map]);
  useEffect(() => {
    const dragging = map.dragging.enabled();
    if (boxMode) map.dragging.disable();
    return () => { if (dragging) map.dragging.enable(); };
  }, [boxMode, map]);
  useMapEvents({
    mousedown: (event) => {
      if (!boxMode || event.originalEvent?.button !== 0) return;
      start.current = event.latlng;
      setBox([event.latlng, event.latlng]);
    },
    mousemove: (event) => {
      if (boxMode && start.current) setBox([start.current, event.latlng]);
    },
    mouseup: (event) => {
      if (!boxMode || !start.current) return;
      const bounds = L.latLngBounds(start.current, event.latlng);
      start.current = null;
      setBox(null);
      onDone();
      if (bounds.getWest() < bounds.getEast() && bounds.getSouth() < bounds.getNorth()) onBox(bounds.toBBoxString());
    },
    mouseout: () => { start.current = null; setBox(null); },
  });
  return box ? <Rectangle bounds={box} interactive={false} pathOptions={{ color: '#2563eb', dashArray: '5 5' }} /> : null;
}

export default function LakeSelectionMap({
  layer, tenantId, geometry, geometryKey, selectedIds = [], onToggleLake, onAddLakes, onViewportChange, disabled = false, featureNoun = 'lakes',
}) {
  const [boxMode, setBoxMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);
  const addLakes = useRef(onAddLakes);
  addLakes.current = onAddLakes;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const selectBox = async (bbox) => {
    setBusy(true);
    setError('');
    try {
      const response = await searchManagedGeographyFeatures(layer.layerId, {
        tenantId, module: 'species_stewardship', bbox, limit: 51,
      });
      if (!alive.current) return;
      if (response.data.length > 50) setError(`More than 50 ${featureNoun} touch that box. Zoom in and draw a smaller box.`);
      else if (response.data.length === 0) setError(`No ${featureNoun} touch that box.`);
      else addLakes.current(response.data);
    } catch (requestError) {
      if (alive.current) setError(requestError.response?.data?.message || 'Could not select lakes in that box.');
    } finally { if (alive.current) setBusy(false); }
  };
  return (
    <section aria-label={featureNoun === 'lakes' ? 'Lake selection map' : 'Source area selection map'}>
      <p>Click shapes or use list checkboxes to select up to 50 {featureNoun}. Drag a box to add intersecting features. Name search looks across the dataset; opening a result zooms to its boundary.</p>
      <button type="button" disabled={busy || disabled} onClick={() => setBoxMode((value) => !value)}>
        {boxMode ? 'Cancel box selection' : `Select ${featureNoun} by box`}
      </button>
      {boxMode && <p>Drag from one corner to the opposite corner on the map.</p>}
      {busy && <p role="status">Finding lakes in the box...</p>}
      {error && <p role="alert">{error}</p>}
      <MapContainer center={[51, -91]} zoom={6} style={{ height: 420, borderRadius: 6, marginTop: 10, cursor: boxMode ? 'crosshair' : 'grab' }}>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <LakeMapActions geometry={geometry} boxMode={boxMode && !disabled} onBox={selectBox} onDone={() => setBoxMode(false)} />
        <LocalLakesLayer layer={layer} tenantId={tenantId} consumerModule="species_stewardship"
          featureNoun={featureNoun}
          interactive={!boxMode && !disabled} onFeatureSelect={onToggleLake} selectedIds={selectedIds} onViewportChange={onViewportChange} />
        {geometry?.coordinates?.length > 0 && <GeoJSON key={geometryKey} data={geometry} interactive={false}
          style={{ color: '#2563eb', weight: 3, fillOpacity: 0.12 }} />}
      </MapContainer>
      <p>Source shapes and the local list load at zoom 11+. Search by name at any zoom.</p>
    </section>
  );
}
